import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArrowRight, Building2, CheckCircle2, Lock, Mail, User, XCircle } from 'lucide-react';
import { zodResolver } from '@/lib/zodResolver';
import { landingRouteFor, useAuthStore } from '@/store/authStore';
import { tenantApi } from '@/api';
import { ApiError } from '@/types';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Alert, FullPageLoader } from '@/components/ui/Feedback';

const schema = z.object({
  companyName: z.string().min(1, 'Company name is required.').max(160),
  slug: z
    .string()
    .min(1, 'Workspace address is required.')
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/, 'Use lowercase letters, digits and hyphens, e.g. bd-apparels.'),
  adminName: z.string().min(1, 'Your name is required.').max(160),
  adminEmail: z.string().min(1, 'Email is required.').email('Enter a valid email address.'),
  adminPassword: z.string().min(8, 'Use at least 8 characters.'),
  country: z.string().optional(),
});

type RegisterForm = z.infer<typeof schema>;

/** `MyCompany Ltd.` -> `mycompany-ltd`, kept in sync until the user edits the slug themselves. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export function RegisterPage() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const slugEditedManually = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver<typeof schema, RegisterForm>(schema),
    defaultValues: { companyName: '', slug: '', adminName: '', adminEmail: '', adminPassword: '', country: '' },
  });

  const companyName = watch('companyName');
  const slug = watch('slug');

  // Keep the slug derived from the company name until the visitor types into
  // the slug field directly — the same "auto then override" pattern most
  // sign-up forms use for a URL/handle field.
  useEffect(() => {
    if (!slugEditedManually.current) {
      setValue('slug', slugify(companyName), { shouldValidate: false });
    }
  }, [companyName, setValue]);

  // Debounced live availability check — checked on every valid change, not on blur,
  // so the visitor sees the answer before they even reach the next field.
  useEffect(() => {
    if (!slug || !/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/.test(slug)) {
      setSlugStatus('idle');
      return;
    }
    setSlugStatus('checking');
    const timer = setTimeout(() => {
      tenantApi
        .slugAvailable(slug)
        .then((result) => setSlugStatus(result.available ? 'available' : 'taken'))
        .catch(() => setSlugStatus('idle'));
    }, 400);
    return () => clearTimeout(timer);
  }, [slug]);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await tenantApi.register({
        ...values,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      // The account we just registered — sign straight into it rather than
      // sending the visitor back to a second form with the same fields.
      const user = await login({ email: values.adminEmail, password: values.adminPassword, rememberMe: true });
      setSigningIn(true);
      navigate(landingRouteFor(user), { replace: true });
    } catch (error) {
      setSigningIn(false);
      if (error instanceof ApiError && error.fieldErrors) {
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          setError(field as keyof RegisterForm, { message });
        }
        return;
      }
      setFormError(error instanceof Error ? error.message : 'Could not create your workspace. Please try again.');
    }
  });

  return (
    <div className="grid h-screen lg:grid-cols-[40%_60%]">
      {signingIn && <FullPageLoader label="Setting up your workspace…" sublabel="Signing you in" />}

      <main className="relative flex items-center justify-center overflow-y-auto bg-gradient-to-b from-brand-50/70 via-white to-brand-50/40 px-5 py-10 sm:px-8">
        <span className="pointer-events-none absolute top-1/3 -left-16 size-64 rounded-full bg-brand-100/60 blur-3xl" aria-hidden />

        <div className="relative w-full max-w-[420px]">
          <div className="mb-8 flex flex-col items-center text-center">
            <img src="/asset/image/logo.png" alt="BD Design Pvt. Ltd." className="size-40 object-contain" />
            <h1 className="text-3xl font-extrabold tracking-tight text-navy-900">BD Design Pvt. Ltd.</h1>
            <p className="mt-1 text-m font-semibold text-brand-600">Carton QR Tracking System</p>
          </div>

          <div className="rounded-2xl border border-line bg-white p-6 shadow-pop sm:p-8">
            <h2 className="text-xl font-bold tracking-tight text-ink">Create your workspace</h2>
            <p className="mt-1 text-sm text-ink-muted">Set up your company in a couple of minutes</p>

            {formError && (
              <Alert tone="error" className="mt-5">
                {formError}
              </Alert>
            )}

            <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4" noValidate>
              <Field label="Company name" htmlFor="companyName" required error={errors.companyName?.message}>
                <Input
                  id="companyName"
                  type="text"
                  autoComplete="organization"
                  placeholder="BD Apparels Ltd"
                  leftIcon={<Building2 className="size-4" aria-hidden />}
                  error={errors.companyName?.message}
                  {...register('companyName')}
                />
              </Field>

              <Field
                label="Workspace address"
                htmlFor="slug"
                required
                error={errors.slug?.message}
                hint={!errors.slug && slug ? `Your team signs in at ${slug}.cartonqr.example.com` : undefined}
              >
                <Input
                  id="slug"
                  type="text"
                  autoComplete="off"
                  placeholder="bd-apparels"
                  error={errors.slug?.message}
                  rightSlot={
                    slugStatus === 'checking' ? (
                      <span className="size-3.5 animate-spin rounded-full border-2 border-ink-muted border-t-transparent" aria-hidden />
                    ) : slugStatus === 'available' ? (
                      <CheckCircle2 className="size-4 text-receive-600" aria-hidden />
                    ) : slugStatus === 'taken' ? (
                      <XCircle className="size-4 text-red-500" aria-hidden />
                    ) : undefined
                  }
                  {...register('slug', {
                    onChange: () => {
                      slugEditedManually.current = true;
                    },
                  })}
                />
              </Field>

              <Field label="Your name" htmlFor="adminName" required error={errors.adminName?.message}>
                <Input
                  id="adminName"
                  type="text"
                  autoComplete="name"
                  placeholder="Jane Doe"
                  leftIcon={<User className="size-4" aria-hidden />}
                  error={errors.adminName?.message}
                  {...register('adminName')}
                />
              </Field>

              <Field label="Work email" htmlFor="adminEmail" required error={errors.adminEmail?.message}>
                <Input
                  id="adminEmail"
                  type="email"
                  autoComplete="email"
                  placeholder="jane@bdapparels.com"
                  leftIcon={<Mail className="size-4" aria-hidden />}
                  error={errors.adminEmail?.message}
                  {...register('adminEmail')}
                />
              </Field>

              <Field label="Password" htmlFor="adminPassword" required error={errors.adminPassword?.message}>
                <Input
                  id="adminPassword"
                  type="password"
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  leftIcon={<Lock className="size-4" aria-hidden />}
                  error={errors.adminPassword?.message}
                  {...register('adminPassword')}
                />
              </Field>

              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={isSubmitting}
                disabled={slugStatus === 'taken'}
                rightIcon={!isSubmitting ? <ArrowRight className="size-4" aria-hidden /> : undefined}
                className="mt-1 bg-brand-700 hover:bg-brand-800"
              >
                Create workspace
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-ink-muted">
            Already have a workspace?{' '}
            <Link to="/login" className="font-semibold text-brand-600 hover:underline">
              Sign in
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-ink-muted">
            {new Date().getFullYear()} © Presented by <span className="font-bold text-ink-soft">Suffix IT Limited</span>
          </p>
        </div>
      </main>

      <aside className="hidden overflow-hidden bg-gradient-to-br from-brand-100 via-brand-50 to-white lg:block">
        <div className="flex h-full w-full items-center justify-center">
          <img src="/asset/image/warehouse.png" alt="Warehouse Illustration" className="h-full w-full object-cover" />
        </div>
      </aside>
    </div>
  );
}
